import { Typography, TypographyProps } from '@mui/material';
import { TimeUnits } from 'convert-units/definitions/time';
import { Duration, formatDuration, intervalToDuration, sub } from 'date-fns';
import { camelCase, isNil, isUndefined } from 'lodash';
import { useMemo } from 'react';

import { DeploymentConfig } from '@/api/devices';
import { ConvertibleUnitPreferenceTypes, ConvertibleUnitPreferences, useDisplayPreferences } from '@/api/userAccount';
import { Measures, Units, convert } from '@/common/convert';

export type DatumUnit = Units | 'unitless';

export type QuantityType = ConvertibleUnitPreferenceTypes | Measures;

export type DatumContext = {
    deploymentConfig?: DeploymentConfig;
};

export type DatumParams = {
    value?: number | null;
    defaultValue?: number;
    maxDecimalPlaces?: number | null;
    noGrouping?: boolean;
    padTrailingZeros?: boolean;
    hideUnit?: boolean;
    minimumDisplayValue?: number;
    context?: DatumContext;
} & (
    | {
          sourceUnit: DatumUnit;
          targetUnit?: DatumUnit | null;
          type: QuantityType;
      }
    | {
          sourceUnit?: never;
          targetUnit?: never;
          type: 'unitless';
      }
);

const DEFAULT_MAX_DECIMAL_PLACES = 3;

const defaultMaxDecimalPlacesPerUnit: Partial<Record<DatumUnit, number>> = {
    deg: 0,
    s: 1,
    hPa: 1,
} as const;

const defaultMaxDecimalPlacesPerType: Partial<Record<QuantityType | 'unitless', number>> = {
    temperature: 2,
    soundPressureLevel: 1,
    salinity: 3,
} as const;

const unitLabels: Partial<Record<DatumUnit, string>> = {
    unitless: '',
    ratio: '',
    deg: '°',
    F: '°F',
    C: '°C',
    'mH20-salt': 'm depth',
    'mH20-fresh': 'm depth',
    'dB re 1 µPa²': 'dB',
    'dB re µPa²': 'dB',
};

export function renderUnit(unit: DatumUnit) {
    return unitLabels[unit] ?? unit.replaceAll(/\^2/g, '²').replaceAll(/\^3/g, '³').replaceAll(/\*/g, '⋅');
}

const noSpace: Partial<Record<DatumUnit, boolean>> = {
    unitless: true,
    deg: true,
    '%': true,
};

/* Usually a space, with a few exceptions */
function unitPrefix(unit: DatumUnit) {
    if (noSpace[unit]) {
        return '';
    }
    return ' ';
}

export const unitType = <D,>(
    units: { unitType: string } | { unitTypeKey: keyof D | string },
    datum: D
): QuantityType => {
    if ('unitType' in units) {
        return units.unitType as QuantityType;
    }
    return datum[units.unitTypeKey as keyof D] as QuantityType;
};

export const unit = <D,>(units: { unit: string } | { unitKey: keyof D | string }, datum: D): DatumUnit => {
    if ('unit' in units) {
        return units.unit as DatumUnit;
    }
    return datum[units.unitKey as keyof D] as DatumUnit;
};

export const normalizeQuantityType = (type: string): QuantityType => {
    const typeAliases: Record<string, QuantityType> = {
        barometric_pressure: 'airPressure', // barometric_pressure is used in device alerts API response
        tilt: 'angle',
    };
    return typeAliases[type] ?? (camelCase(type) as QuantityType);
};

/**
 *
 * @param unitPreferences is `| null` rather than ?/undefined, even though it means you have to `?? null` if you
 *   want to pass useDisplayPreferences(), because it prevents callers from accidentally omitting it completely,
 *   which would make this whole thing pointless.
 */
export const formatDatum = ({
    value,
    sourceUnit,
    targetUnit,
    type,
    defaultValue,
    hideUnit,
    context,
    maxDecimalPlaces,
    noGrouping,
    padTrailingZeros,
    minimumDisplayValue,
    unitPreferences,
}: DatumParams & { unitPreferences: ConvertibleUnitPreferences | null }) => {
    const quantityType = normalizeQuantityType(type);

    if (!targetUnit && unitPreferences && quantityType in unitPreferences) {
        targetUnit = unitPreferences[quantityType as ConvertibleUnitPreferenceTypes];
    }
    sourceUnit = unitInContext(quantityType, sourceUnit, context) ?? sourceUnit;
    targetUnit = unitInContext(quantityType, targetUnit, context);

    let renderedValue;
    let renderedUnit: DatumUnit | undefined;
    let sourceValue = value;
    if (isNil(sourceValue)) {
        if (isNil(defaultValue)) {
            return null;
        }
        sourceValue = defaultValue;
    }

    if (sourceUnit && targetUnit && targetUnit !== sourceUnit) {
        try {
            renderedValue = convert(sourceValue).from(sourceUnit).to(targetUnit);
            renderedUnit = targetUnit;
        } catch (err) {
            console.error(`Error converting ${sourceUnit} to ${targetUnit}`, err);
            renderedValue = sourceValue;
            renderedUnit = sourceUnit;
        }
    } else {
        renderedValue = sourceValue;
        renderedUnit = sourceUnit;
    }

    if (isNil(renderedValue) || isNaN(renderedValue)) {
        return null;
    }

    if (quantityType === 'time' && isNil(maxDecimalPlaces) && !targetUnit && !hideUnit && renderedUnit) {
        // if it's `time` (e.g. interval, not timestamp), and no explicit target or formatting parameters are set, render as a multi-unit duration
        const dateFnKeyForUnit: { [timeUnit in TimeUnits]?: keyof Duration } = {
            d: 'days',
            s: 'seconds',
            min: 'minutes',
            h: 'hours',
            week: 'weeks',
            month: 'months',
            year: 'years',
        };
        if (renderedUnit === 'ms') {
            renderedUnit = 's';
            renderedValue = renderedValue / 1000;
        }
        if (renderedUnit in dateFnKeyForUnit) {
            // using new Date rather than useNow and not worrying about TZ... almost all consumers of this method don't render anything time-related,
            // so don't want to require them as parameters, and we're just using now to help componentize a duration (how many months is xx days?),
            // so we don't need to be reactive and the TZ isn't going to make a difference here.
            const now = new Date();
            return formatDuration(
                intervalToDuration({
                    start: sub(now, {
                        [dateFnKeyForUnit[renderedUnit as keyof typeof dateFnKeyForUnit] as keyof Duration]:
                            renderedValue,
                    }),
                    end: now,
                })
            );
        }
    }

    const maxDecimals = getMaxDecimalPlaces(maxDecimalPlaces, renderedUnit, quantityType);

    const isValueSmallerThanMinimum = !isNil(minimumDisplayValue) && renderedValue < minimumDisplayValue;
    const valueToFormat = isValueSmallerThanMinimum ? minimumDisplayValue : renderedValue;

    const formattedValue = new Intl.NumberFormat(undefined, {
        useGrouping: !noGrouping,
        minimumFractionDigits: padTrailingZeros ? (maxDecimals ?? 0) : undefined,
        maximumFractionDigits: maxDecimals ?? 100,
    }).format(+valueToFormat);

    const formattedUnit = !hideUnit && renderedUnit ? `${unitPrefix(renderedUnit)}${renderUnit(renderedUnit)}` : '';

    return `${isValueSmallerThanMinimum ? '<' : ''}${formattedValue}${formattedUnit}`;
};

export type DatumComponentParams = DatumParams & Pick<TypographyProps<'span'>, 'sx' | 'variant'>;

export const Datum = ({
    value,
    sourceUnit,
    targetUnit,
    type,
    defaultValue,
    hideUnit,
    context,
    maxDecimalPlaces,
    padTrailingZeros,
    minimumDisplayValue,
    sx,
    variant,
}: DatumComponentParams) => {
    const { data: unitPreferences } = useDisplayPreferences();
    const formattedDatum = useMemo(
        () =>
            // we want to list all the members rather than just destructure out the {...props} so that
            // we can generate the reactive dependency list. typescript doesn't remember that the units
            // are only undefined with `unitless`, so we cast them into the unitful case, just to make
            // the compiler happy. formatDatum is also expecting a DatumParams so it will all work out.
            formatDatum({
                unitPreferences: unitPreferences ?? null,
                value,
                sourceUnit: sourceUnit as DatumUnit,
                targetUnit: targetUnit as DatumUnit,
                type: type as QuantityType,
                defaultValue,
                hideUnit,
                context,
                maxDecimalPlaces,
                padTrailingZeros,
                minimumDisplayValue,
            }),
        [
            context,
            defaultValue,
            hideUnit,
            maxDecimalPlaces,
            minimumDisplayValue,
            padTrailingZeros,
            sourceUnit,
            targetUnit,
            type,
            unitPreferences,
            value,
        ]
    );

    return (
        <Typography component="span" sx={{ whiteSpace: 'nowrap', ...sx }} variant={variant}>
            {formattedDatum}
        </Typography>
    );
};

export const unitInContext = (
    type: QuantityType | 'unitless',
    unit: DatumUnit | undefined | null,
    context: DatumContext = {}
): DatumUnit | null => {
    if (type === 'pressure' && unit === 'm') {
        if (!context.deploymentConfig?.waterType) {
            console.warn("Can't convert pressure to depth without water type");
            // null targetUnit means default to sourceUnit
            return null;
        }
        switch (context.deploymentConfig?.waterType) {
            case 'Freshwater':
                return 'mH20-fresh';
            case 'Saltwater':
                return 'mH20-salt';
        }
    } else {
        return unit ?? null;
    }
};

/**
 * If `maxDecimalPlaces` is not undefined, returns it (null means no limit).
 * Otherwise, picks a default based on the unit and data type.
 */
export function getMaxDecimalPlaces(
    maxDecimalPlaces: number | null | undefined,
    unit: DatumUnit | undefined,
    type: QuantityType | 'unitless'
) {
    // null means no limit, undefined means default
    if (!isUndefined(maxDecimalPlaces)) {
        return maxDecimalPlaces;
    }

    if (unit && unit in defaultMaxDecimalPlacesPerUnit) {
        return defaultMaxDecimalPlacesPerUnit[unit];
    }

    return defaultMaxDecimalPlacesPerType[type] ?? DEFAULT_MAX_DECIMAL_PLACES;
}
